import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import {
	BaseNAICSCode,
	isSetOverlap,
	NAICSCodeGroupMap,
	type NAICSCodeGroupSubCode,
	type NaicsCodeId,
	UnwrapMap,
} from "@inrev/common";
import { useNaicsCodeGroupMap } from "@portal/api/naics-code";
import { NAICSCodesList } from "@portal/views/admin/shared/NAICSCodeList";
import { RankingInfo, rankItem, rankings } from "@tanstack/match-sorter-utils";
import { useIsFirstRender } from "@uidotdev/usehooks";
import { type FC, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { FieldError, Merge } from "react-hook-form";
import { Modal } from "../../layout/Modal";
import { cn } from "../../lib/utils";
import { Icon, Text } from "../../primitives";
import { Button } from "../../ui/Button";
import { Checkbox } from "../../ui/Checkbox";
import { MultiSelectContent, MultiSelectSearch } from "../../ui/GroupMultiSelect";
import { FormFieldError } from "../FormFieldError";

export type NAICSCodeSelectInputProps = {
	value: NaicsCodeId[];
	title: string;
	className?: string;
	buttonClassName?: string;
	buttonIconClassName?: string;
	listClassName?: string;
	panelClassName?: string;
	modalClassName?: string;
	selectText: string;
	editText: string;
	searchPlaceholder?: string;
	error?: FieldError | Merge<FieldError, (FieldError | undefined)[]>;
	errorMessage?: string;
	readonly?: boolean;
	onChange: (value: NaicsCodeId[]) => void;
	onBlur?: () => void;
	subsetFilters?: [
		{ label: string; subset: NaicsCodeId[] },
		...{ label: string; subset: NaicsCodeId[] }[],
	];
	defaultSubsetIndex?: number;
	allNAICSCodesLabel?: string;
	inline?: boolean;
	/**
	 * Controlled open state for the picker modal. When provided the component renders headless —
	 * only the modal — so the consumer supplies its own trigger and code-list display.
	 */
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
} & (
	| {
			subsetFilters: [
				{ label: string; subset: NaicsCodeId[] },
				...{ label: string; subset: NaicsCodeId[] }[],
			];
			defaultSubsetIndex?: number;
			allNAICSCodesLabel: string;
	  }
	| {
			subsetFilters?: undefined;
			defaultSubsetIndex?: undefined;
			allNAICSCodesLabel?: undefined;
	  }
);

const rankSearch = (haystack: string, searchString: string) =>
	rankItem(haystack, searchString, {
		accessors: [
			{
				accessor: (data) => data,
				threshold: rankings.WORD_STARTS_WITH,
				maxRanking: rankings.EQUAL,
			},
		],
	});

const basesForSelectedIds = (
	ids: readonly NaicsCodeId[],
	groupMap: NAICSCodeGroupMap,
): Set<BaseNAICSCode> => {
	const selected = new Set(ids);
	const bases = new Set<BaseNAICSCode>();
	groupMap.forEach((group, base) => {
		group.subCodes.forEach((_, id) => {
			if (selected.has(id)) bases.add(base);
		});
	});
	return bases;
};

const fuzzyMatchAndSort = (
	groupMap: NAICSCodeGroupMap,
	searchString: string,
	subsetFilterSet?: Set<NaicsCodeId>,
) => {
	const resultMap: NAICSCodeGroupMap = new Map();
	let matchedGroups: [RankingInfo, RankingInfo, ...UnwrapMap<NAICSCodeGroupMap>, boolean][] = [];
	let subCodes: [RankingInfo, NaicsCodeId, NAICSCodeGroupSubCode][] = [];
	groupMap.forEach((group, baseNaicsCode) => {
		const groupRanking = rankSearch(group.label, searchString);
		subCodes = [];
		group.subCodes.forEach((entry, id) => {
			if (subsetFilterSet && !subsetFilterSet.has(id)) return;
			const subCodeRanking = rankSearch(`${entry.code} ${entry.label}`, searchString);
			if (subCodeRanking.passed || groupRanking.passed) {
				subCodes.push([subCodeRanking, id, entry]);
			}
		});
		subCodes = subCodes.sort((a, b) => (a[0].rank > b[0].rank ? -1 : 1));
		if (subCodes.length) {
			matchedGroups.push([
				groupRanking,
				subCodes[0][0],
				baseNaicsCode,
				{
					label: group.label,
					subCodes: new Map(subCodes.map(([_, id, entry]) => [id, entry])),
				},
				false,
			]);
		}
	});
	matchedGroups = matchedGroups.sort((a, b) => {
		const aMax = Math.max(a[0].rank, a[1].rank);
		const bMax = Math.max(b[0].rank, b[1].rank);
		if (aMax > bMax || (aMax === bMax && a[3])) return -1;
		return 1;
	});
	matchedGroups.forEach((entry) => {
		resultMap.set(entry[2], entry[3]);
	});

	return resultMap;
};

function sortByExistenceInSpecialties(
	resultMap: NAICSCodeGroupMap,
	prioritySet: Set<NaicsCodeId> | undefined,
): NAICSCodeGroupMap {
	if (!prioritySet) {
		return resultMap;
	}

	const entries = Array.from(resultMap.entries());
	const existingEntries: UnwrapMap<NAICSCodeGroupMap>[] = [];
	const nonExistingEntries: UnwrapMap<NAICSCodeGroupMap>[] = [];

	for (const entry of entries) {
		let hasPrioritySubcode = false;
		for (const id of entry[1].subCodes.keys()) {
			if (prioritySet.has(id)) {
				hasPrioritySubcode = true;
				break;
			}
		}
		if (hasPrioritySubcode) existingEntries.push(entry);
		else nonExistingEntries.push(entry);
	}

	return new Map([...existingEntries, ...nonExistingEntries]);
}

const NAICSCodeSelectInputGroup = ({
	label,
	size,
	allSelected,
	subsetSelected,
	subCodeSize,
	onSelect,
	onDeselect,
	children,
}: {
	label: string;
	size: number;
	allSelected: boolean;
	subsetSelected: boolean;
	subCodeSize: number;
	onSelect: () => void;
	onDeselect: () => void;
	children: ReactNode;
}) => (
	<Disclosure as="div" className="flex flex-col w-full min-h-fit relative">
		<div className="sticky left-0 top-0 right-0 h-[48px] mb-[-48px] shadow-sm"></div>
		<DisclosureButton
			as="div"
			className="group/expand z-20 flex items-center h-[48px] space-x-[16px] sticky top-0 left-0 right-0 text-[14px] text-secondary font-semibold px-[25px] mb-[-1px] bg-white cursor-pointer"
		>
			<div className="flex items-center h-full">
				<Icon
					type="chevron-right"
					size="mini"
					filled
					className="text-gray-400 group-hover/expand:text-gray-800 ui-open:text-gray-800 ui-open:rotate-90"
				/>
			</div>
			<div className="flex-1 flex items-center space-x-[10px]">
				<Checkbox
					checked={allSelected}
					partialChecked={subsetSelected}
					onClick={(e) => {
						e.stopPropagation();
						allSelected || subsetSelected ? onDeselect() : onSelect();
					}}
				/>
				<Text type="body-strong" color="secondary">
					{label}
				</Text>
			</div>
			{size > 0 && (
				<Text type="caption" color="tertiary">
					{size}/{subCodeSize}
				</Text>
			)}
		</DisclosureButton>
		<DisclosurePanel className="relative flex-1 flex flex-col mt-[3px] mx-[20px]">
			{children}
			<div className="absolute left-[-20px] right-[-20px] top-[-2px] h-[2px] bg-white"></div>
		</DisclosurePanel>
	</Disclosure>
);

const NAICSCodeSelectInputSubCodeOption = ({
	label,
	selected,
	onSelect,
	onDeselect,
	optionClassName,
}: {
	label: string;
	selected: boolean;
	onSelect: () => void;
	onDeselect: () => void;
	optionClassName?: string;
}) => (
	<div
		className={cn(
			"group flex items-center text-gray-800 w-full py-[10px] pl-[20px] pr-[10px] mb-[-1px] [&:nth-last-child(3)]:mb-0 space-x-[15px] hover:bg-gray-100 border-x border-b first:border-t border-gray-200 cursor-pointer hover:outline hover:outline-[1px] hover:outline-offset-[-1px] hover:outline-gray-300",
			optionClassName,
		)}
		onClick={() => {
			selected ? onDeselect() : onSelect();
		}}
	>
		<Checkbox checked={selected} />
		<div className="h-fit flex-1 text-[15px] text-gray-800 font">{label}</div>
	</div>
);

const onGroupSelect = (
	group: UnwrapMap<NAICSCodeGroupMap>[1],
	value: NaicsCodeId[],
	onChange: (value: NaicsCodeId[]) => void,
) => {
	onChange([...new Set([...value, ...group.subCodes.keys()])]);
};

const onGroupDeselect = (
	group: UnwrapMap<NAICSCodeGroupMap>[1],
	value: NaicsCodeId[],
	onChange: (value: NaicsCodeId[]) => void,
) => {
	const toRemove = new Set(group.subCodes.keys());
	onChange(value.filter((id) => !toRemove.has(id)));
};

const onSubCodeSelect = (
	id: NaicsCodeId,
	value: NaicsCodeId[],
	onChange: (value: NaicsCodeId[]) => void,
) => {
	onChange([...new Set([...value, id])]);
};

const onSubCodeDeselect = (
	id: NaicsCodeId,
	value: NaicsCodeId[],
	onChange: (value: NaicsCodeId[]) => void,
) => {
	onChange(value.filter((selected) => selected !== id));
};

const getGroupComponents = (
	groupMap: NAICSCodeGroupMap,
	baseNAICSCodeSet: Set<BaseNAICSCode>,
	value: NaicsCodeId[],
	valueSet: Set<NaicsCodeId>,
	onChange: (value: NaicsCodeId[]) => void,
) => {
	const groupComponents: ReactNode[] = [];
	let groupKey = 0;
	groupMap.forEach((group, baseNaicsCode) => {
		if (
			(() => {
				if (typeof baseNaicsCode === "string") {
					if (baseNAICSCodeSet.has(baseNaicsCode)) {
						return true;
					}
				} else if (isSetOverlap(baseNAICSCodeSet, baseNaicsCode)) {
					return true;
				}
				return false;
			})()
		) {
			const subCodeComponents: ReactNode[] = [];
			let subCodeKey = 0;
			let selectedSubCodeCount = 0;
			group.subCodes.forEach((entry, id) => {
				const selected = valueSet.has(id);
				if (selected) selectedSubCodeCount++;
				subCodeComponents.push(
					<NAICSCodeSelectInputSubCodeOption
						key={++subCodeKey}
						label={entry.label}
						selected={selected}
						onSelect={() => onSubCodeSelect(id, value, onChange)}
						onDeselect={() => onSubCodeDeselect(id, value, onChange)}
					/>,
				);
			});
			groupComponents.push(
				<NAICSCodeSelectInputGroup
					key={++groupKey}
					label={group.label}
					size={selectedSubCodeCount}
					subCodeSize={group.subCodes.size}
					allSelected={selectedSubCodeCount === group.subCodes.size}
					subsetSelected={selectedSubCodeCount !== group.subCodes.size}
					onSelect={() => onGroupSelect(group, value, onChange)}
					onDeselect={() => onGroupDeselect(group, value, onChange)}
				>
					{subCodeComponents}
					<div className="w-full h-[12px] z-30 bg-white shrink-0"></div>
				</NAICSCodeSelectInputGroup>,
			);
		} else {
			const subCodeComponents: ReactNode[] = [];
			let subCodeKey = 0;
			group.subCodes.forEach((entry, id) => {
				subCodeComponents.push(
					<NAICSCodeSelectInputSubCodeOption
						key={++subCodeKey}
						label={entry.label}
						selected={false}
						onSelect={() => onSubCodeSelect(id, value, onChange)}
						onDeselect={() => onSubCodeSelect(id, value, onChange)}
					/>,
				);
			});
			groupComponents.push(
				<NAICSCodeSelectInputGroup
					key={++groupKey}
					label={group.label}
					size={0}
					subCodeSize={group.subCodes.size}
					allSelected={false}
					subsetSelected={false}
					onSelect={() => onGroupSelect(group, value, onChange)}
					onDeselect={() => onGroupDeselect(group, value, onChange)}
				>
					{subCodeComponents}
					<div className="w-full h-[12px] z-30 bg-white shrink-0"></div>
				</NAICSCodeSelectInputGroup>,
			);
		}
	});
	return groupComponents;
};

const NAICSCodeSubsetFilterListItem = ({
	label,
	selected,
	onClick,
}: {
	label: string;
	selected: boolean;
	onClick: () => void;
}) => (
	<div
		className={cn(
			selected
				? "flex items-center px-[12px] py-[8px] rounded cursor-pointer bg-brand-primary text-[13px] font-medium text-gray-50 shadow-md"
				: "flex items-center px-[12px] py-[8px] rounded cursor-pointer bg-gray-50 hover:bg-gray-200/50 text-[13px] font-medium text-secondary",
		)}
		onClick={onClick}
	>
		<span className="flex-1">{label}</span>
		<Icon type="chevron-double-right" size="micro" filled />
	</div>
);

export const NAICSCodeSelectInput: FC<NAICSCodeSelectInputProps> = ({
	title,
	subsetFilters,
	defaultSubsetIndex,
	allNAICSCodesLabel,
	className,
	buttonClassName,
	listClassName,
	panelClassName,
	editText,
	error,
	errorMessage,
	readonly,
	onChange,
	onBlur,
	inline,
	open: controlledOpen,
	onOpenChange,
	value,
	searchPlaceholder,
}) => {
	const groupMap = useNaicsCodeGroupMap();
	const isFirstRender = useIsFirstRender();
	const isControlled = controlledOpen !== undefined;
	const [internalOpen, setInternalOpen] = useState<boolean>(false);
	const open = isControlled ? controlledOpen : internalOpen;
	const wasOpenRef = useRef(false);
	const onBlurRef = useRef(onBlur);
	onBlurRef.current = onBlur;
	const setOpen = (next: boolean) => {
		if (isControlled) onOpenChange?.(next);
		else setInternalOpen(next);
	};
	const subsetFilterSets = useMemo(
		() => subsetFilters?.map(({ subset }) => new Set(subset)),
		[subsetFilters],
	);
	const [selectedSubsetFilterIndex, setSelectedSubsetFilterIndex] = useState<number | undefined>(
		defaultSubsetIndex,
	);
	const [valuesChanged, setValuesChanged] = useState<boolean>(false);
	const [searchValue, setSearchValue] = useState<string>("");
	const valueSet = useMemo(() => new Set(value), [value]);
	const valueToBaseNaicsCodes = useMemo(
		() => basesForSelectedIds(value, groupMap),
		[value, groupMap],
	);

	const groupComponents = useMemo(() => {
		if (searchValue !== "" || selectedSubsetFilterIndex !== undefined) {
			const filteredCodes = fuzzyMatchAndSort(
				groupMap,
				searchValue,
				selectedSubsetFilterIndex !== undefined && subsetFilterSets !== undefined
					? subsetFilterSets[selectedSubsetFilterIndex]
					: undefined,
			);
			const subset = subsetFilterSets !== undefined ? subsetFilterSets[0] : undefined;

			return getGroupComponents(
				sortByExistenceInSpecialties(filteredCodes, subset),
				valueToBaseNaicsCodes,
				value,
				valueSet,
				onChange,
			);
		}
		return getGroupComponents(groupMap, valueToBaseNaicsCodes, value, valueSet, onChange);
	}, [
		groupMap,
		subsetFilterSets,
		selectedSubsetFilterIndex,
		searchValue,
		valueSet,
		valueToBaseNaicsCodes,
		onChange,
		value,
	]);

	useEffect(() => {
		if (wasOpenRef.current && !open) {
			onBlurRef.current?.();
		}
		wasOpenRef.current = open;
		setValuesChanged(false);
	}, [open]);

	useEffect(() => {
		if (!isFirstRender && !valuesChanged) {
			setValuesChanged(true);
		}
	}, [value]);

	useEffect(() => {
		if (!isFirstRender)
			if (searchValue === "") {
				setSelectedSubsetFilterIndex(defaultSubsetIndex);
			} else setSelectedSubsetFilterIndex(undefined);
	}, [searchValue]);

	useEffect(() => {
		if (
			subsetFilters &&
			selectedSubsetFilterIndex !== undefined &&
			subsetFilters[selectedSubsetFilterIndex].subset.length === 0
		) {
			setSelectedSubsetFilterIndex(undefined);
		}
	}, [subsetFilters]);

	return (
		<div className={cn("relative h-fit w-full", className)}>
			{!isControlled && (
				<div
					className={cn(
						"flex flex-col space-y-[20px] py-[5px]",
						inline && "flex-row items-center justify-start space-x-[10px] space-y-[5px] py-0",
					)}
				>
					<NAICSCodesList
						naicsCodes={value}
						showCount
						className={cn(inline && "min-w-[360px] w-full", listClassName)}
						panelClassName={panelClassName}
					/>
					{!readonly && (
						<Button
							color="inverse"
							filled={!value.length}
							thinFont
							onClick={() => setOpen(true)}
							disabled={readonly}
							className={cn(
								"w-fit border border-bc-default",
								!!value.length && "text-primary enabled:hover:text-primary",
								buttonClassName,
							)}
						>
							<div className="flex items-center space-x-[8px]">
								{!!value.length && (
									<>
										<Icon type="pencil-square" size="mini" />
										{!inline && <span>{editText}</span>}
									</>
								)}
								{!value.length && (
									<>
										<Icon type="plus" size="mini" filled />
										{!inline && <span>{editText}</span>}
									</>
								)}
							</div>
						</Button>
					)}
				</div>
			)}
			{open && (
				<Modal onClickOutside={() => setOpen(false)}>
					<div
						className={cn(
							"flex flex-col min-w-full h-[85vh] overflow-y-auto mt-[20px] text-gray-700 font-normal rounded-md bg-white shadow-lg z-[10] select-none",
							!!subsetFilters && subsetFilters.length ? "w-[800px]" : "w-[600px]",
						)}
					>
						<MultiSelectSearch
							setSearchValue={setSearchValue}
							valuesChanged={valuesChanged}
							onSaveClick={setOpen}
							title={title}
							placeholder={searchPlaceholder ?? "Search NAICS codes"}
						/>
						<MultiSelectContent groupComponents={groupComponents} hasFilters={!!subsetFilters}>
							{!!subsetFilters && (
								<>
									{subsetFilters.map(({ label }, index) => (
										<NAICSCodeSubsetFilterListItem
											key={index}
											label={label}
											selected={index === selectedSubsetFilterIndex}
											onClick={() => setSelectedSubsetFilterIndex(index)}
										/>
									))}
									<NAICSCodeSubsetFilterListItem
										label={allNAICSCodesLabel}
										selected={selectedSubsetFilterIndex === undefined}
										onClick={() => setSelectedSubsetFilterIndex(undefined)}
									/>
								</>
							)}
						</MultiSelectContent>
					</div>
				</Modal>
			)}
			<FormFieldError error={error} errorMessage={errorMessage} />
		</div>
	);
};
